import { useMemo } from 'react';
import { ProductType } from '../types';
import { getTooltipText, getSavingsText, calculateTotalSavings, getBonusCountText } from '../config/savingsConfig';
import { getBenefitShortName } from '../config/slidesConfig';

interface UseSavingsCalculatorReturn {
  tooltipText: string;
  getBenefitText: (slideId: string) => string | null;
  getTotalSavings: (slideId: string) => string;
  getNextButtonText: (currentSlideId: string) => string;
  getBonusCountText: (slideId: string) => string;
}

/**
 * Hook per calcolare i risparmi dinamici in base al prodotto e CAP
 * 
 * NUOVA VERSIONE: Usa il database JSON dei CAP per ottenere valori specifici
 * per ogni comune italiano, inclusi bonus regionali e provinciali.
 * 
 * @param productType - Tipo di prodotto (Photovoltaic, Heatpump, Combo)
 * @param zipFlag - (deprecato, mantenuto per compatibilità)
 * @param zipCode - Codice CAP per lookup dinamico dei bonus
 */
export function useSavingsCalculator(
  productType?: ProductType,
  zipFlag?: boolean | null,
  zipCode?: string
): UseSavingsCalculatorReturn {
  // Tooltip text basato sul tipo di prodotto
  const tooltipText = useMemo(() => {
    return getTooltipText(productType);
  }, [productType]);

  // Funzione per ottenere il testo del beneficio per una slide
  // Usa il CAP per lookup dinamico dei valori dal database
  const getBenefitText = useMemo(() => {
    return (slideId: string) => getSavingsText(slideId, productType, zipFlag, zipCode);
  }, [productType, zipFlag, zipCode]);

  // Funzione per ottenere il totale cumulativo per una slide
  const getTotalSavings = useMemo(() => {
    return (slideId: string) => calculateTotalSavings(slideId, productType, zipFlag, zipCode);
  }, [productType, zipFlag, zipCode]);

  // Funzione per ottenere il testo del bottone "Avanti" personalizzato
  const getNextButtonText = useMemo(() => {
    return (currentSlideId: string) => {
      const buttonMapping: Record<string, string> = {
        name_form: 'email_form',
        email_form: 'phone_form',
      };

      const nextSlideId = buttonMapping[currentSlideId];
      if (!nextSlideId) return 'Avanti';

      const nextBenefitText = getSavingsText(nextSlideId, productType, zipFlag, zipCode);
      const shortName = getBenefitShortName(nextBenefitText);
      return `Scopri ${shortName}`;
    };
  }, [productType, zipFlag, zipCode]);

  // Funzione per ottenere il testo dinamico del conteggio bonus
  const getBonusCountTextMemo = useMemo(() => {
    return (slideId: string) => getBonusCountText(slideId, productType, zipCode);
  }, [productType, zipCode]);

  return {
    tooltipText,
    getBenefitText,
    getTotalSavings,
    getNextButtonText,
    getBonusCountText: getBonusCountTextMemo,
  };
}
